import { ThemeType } from '@festina-life/fplus-monaco'
import { useThemeContext } from '@festina-life/ui/theme'
import cx from 'classnames'
import { type Jsx, toJsxRuntime } from 'hast-util-to-jsx-runtime'
import { type ComponentProps, type ReactNode, type Ref, useMemo } from 'react'
import { Fragment, jsx, jsxs } from 'react/jsx-runtime'
import type { ShikiTransformer } from 'shiki'

import { useFPlusHighlighter } from '../highlighting/useFPlusHighlighter'
import type { FPlusViewerAlignment } from '../monaco/components/types'
import { languageID } from '../worker/FPlusConfiguration'

export type FPlusViewerProps = {
  code: string
  className?: string
  alignment?: FPlusViewerAlignment
  /**
   * The rendered expression itself. A caller that truncates it with CSS needs
   * this to ask how much of it is hidden.
   */
  ref?: Ref<HTMLPreElement>
}

/**
 * React's JSX runtime, typed the way `toJsxRuntime` expects it. The runtime
 * itself is the one React uses; only the declared prop type differs.
 */
const reactJsxRuntime = { Fragment, jsx: jsx as Jsx, jsxs: jsxs as Jsx }

/**
 * The viewer sits on surfaces that paint their own background, so the theme's
 * editor background is dropped and only the token colours are kept.
 */
const transparentBackground: ShikiTransformer = {
  name: 'fplus-viewer-transparent-background',
  pre(node) {
    node.properties['style'] = String(node.properties['style'] ?? '').replace(
      /background-color:[^;]*;?/,
      '',
    )
  },
}

/**
 * The code font is asked for again on the `code` element rather than only on
 * the `pre` because Preflight resets font features there, which would
 * otherwise strip them back off inside the expression. Asking for the font
 * rather than for a list of features keeps every surface reading an expression
 * the same way: the editable editor asks Monaco for the same set.
 */
const codeFont = 'font-code'

/**
 * A read-only F-plus expression, highlighted by the shared Shiki highlighter
 * and rendered as React elements. It carries no editor lifecycle at all —
 * nothing is created, disposed, measured or laid out — and it is exactly as
 * tall as the expression it shows.
 *
 * It renders synchronously: the highlighter is set up before anything that can
 * show an expression is rendered, so it is always already there.
 */
export const FPlusViewer = ({
  code,
  className,
  alignment = 'left',
  ref,
}: FPlusViewerProps): ReactNode => {
  const { isDarkMode } = useThemeContext()
  const highlighter = useFPlusHighlighter()

  // Held onto across renders: a new component identity here would remount the
  // whole highlighted expression, throwing away and re-attaching its DOM every
  // time anything above it renders.
  const components = useMemo(
    () => ({
      pre: ({
        children,
        className: shikiClassName,
        ...properties
      }: ComponentProps<'pre'>) => (
        // Selecting an expression is not a click on whatever the expression
        // happens to sit in, such as a row that opens on click.
        // oxlint-disable-next-line click-events-have-key-events, no-noninteractive-element-interactions
        <div
          onClick={(event) => event.stopPropagation()}
          className={cx(
            'flex size-full items-center-safe overflow-hidden',
            alignment === 'center' ? 'justify-center-safe' : 'justify-start',
          )}
        >
          <pre
            {...properties}
            ref={ref}
            className={cx(
              shikiClassName,
              'm-0 p-0 font-code text-sm leading-code',
              className,
            )}
          >
            {children}
          </pre>
        </div>
      ),
      code: ({
        children,
        className: shikiClassName,
        ...properties
      }: ComponentProps<'code'>) => (
        <code {...properties} className={cx(shikiClassName, codeFont)}>
          {children}
        </code>
      ),
    }),
    [alignment, className, ref],
  )

  const highlighted = highlighter.codeToHast(code, {
    lang: languageID,
    theme: isDarkMode ? ThemeType.dark : ThemeType.light,
    transformers: [transparentBackground],
  })

  return toJsxRuntime(highlighted, { ...reactJsxRuntime, components })
}
